/*
The non-visual half of components/Table/DataTable: how a column finds its value in a row,
and how a row finds its React key.

Split out of the JSX so it can be tested. The manager's test runner is plain node with no
JSX transform, so anything that has to be covered by a test has to live in a .js file with
no React in it - the same shape as seoFieldStatus, ticketPriceStatus and cmsTabs.

Worth testing because getting it wrong is not a visible glitch, it is a blank screen: the
first version of the consolidated table threw when no column was marked isId, which took
down the whole Logs tab, because the logs query returns no id field for it to use.
*/

// Dot notation like mongo's, so a column can point at 'user.address.city'.
//
// The version this replaces reduced with `(o, p) => (o ? o[p] : defaultValue)`, which fed
// the default back in as the next object and then indexed into it, so the default only
// ever survived when the path broke on its final segment - 'user.email' on a row with no
// user came back undefined, not the default. Both render as nothing, which is why nobody
// noticed. Resolve first, substitute once at the end.
const resolvePath = (object, path, defaultValue = null) => {
  const value = path
    .split('.')
    .reduce((o, p) => (o === null || o === undefined ? undefined : o[p]), object);
  return value === undefined ? defaultValue : value;
};

// fieldFunc wins over field when it returns anything at all - including '' or 0, which is
// why this tests against undefined rather than truthiness.
const readCell = (column, row) => {
  if (column.fieldFunc) {
    const computed = column.fieldFunc(row);
    if (computed !== undefined) return computed;
  }
  if (!column.field) return undefined;
  return column.field.includes('.') ? resolvePath(row, column.field) : row[column.field];
};

// The field marked isId, or null when the tab has no identity to key on.
const findIdKey = (columns) => {
  const idColumn = columns.find(column => column.isId);
  return idColumn ? idColumn.field : null;
};

// A row's React key. Falls back to position when the rows carry no id, which is weaker but
// still stable within a page - paging and sorting refetch and rebuild the body anyway.
const resolveRowKey = (row, idKey, rowIndex) => (
  idKey ? row[idKey] : `row-${rowIndex}`
);

// A cell key only has to be unique within its row, so the column identifies it.
const resolveCellKey = (column, index) => column.field || column.headerName || `column-${index}`;

const defaultFormatValue = ({
  column, value, row, idValue,
}) => {
  switch (column.type) {
    case 'date':
      return value ? new Date(parseInt(value, 10)).toLocaleString() : '';
    case 'boolean':
      return value ? 'X' : '';
    case 'action':
      return column.content(row, idValue);
    case 'string':
    default:
      return value;
  }
};

module.exports = {
  resolvePath,
  readCell,
  findIdKey,
  resolveRowKey,
  resolveCellKey,
  defaultFormatValue,
};
